import { Box, Button, Skeleton, Typography } from '@mui/material';
import { LocalShipping } from '@mui/icons-material';
import { FC, ReactNode, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { searchOrdersRequest } from 'redux/reducers/euDdp/reducer';
import {
	selectOrderDetails,
	selectOrderDetailsLoading,
	selectOrdersSearch,
} from 'redux/reducers/euDdp/selectors';
import { EasTableValue } from 'ui/EasTable/EasTableRow/EasTableValue';
import { ETableStructureType } from 'ui/EasTable/types';
import { getTrackingPageUrl } from '../../utils';
import { EasCard } from 'ui/EasCard';
import { tableStructure } from '../../tableStructure';

const renderValue = (value: string | null, type: ETableStructureType): ReactNode =>
	value ? <EasTableValue value={value} type={type} size="l" /> : <Typography>—</Typography>;

export const ShippingDetails: FC = () => {
	const dispatch = useDispatch();
	const order = useSelector(selectOrderDetails);
	const orderIsLoading = useSelector(selectOrderDetailsLoading);
	const ordersData = useSelector(selectOrdersSearch);

	useEffect(() => {
		if (!order?.order.id) return;
		dispatch(
			searchOrdersRequest({
				pageNumber: 1,
				pageSize: 1,
				filters: [
					{
						fieldName: 'easOrderId',
						value: String(order.order.id),
						operator: '=',
					},
				],
			}),
		);
	}, [order, dispatch]);

	if (orderIsLoading || !order) {
		return <Skeleton variant="rectangular" height="240px" sx={{ borderRadius: '12px', mb: 2 }} />;
	}

	const handleTrackShipment = () => {
		if (!order?.order.tracking_number) return;
		window.open(getTrackingPageUrl(order.order.tracking_number), '_blank');
	};

	return (
		<EasCard
			id="eu-ddp-order-shipping-details"
			title="Shipping Details"
			action={
				<Button
					variant="contained"
					color="primary"
					size="small"
					startIcon={<LocalShipping />}
					onClick={handleTrackShipment}
					sx={{
						height: '26px',
					}}
				>
					Track shipment
				</Button>
			}
		>
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					gap: 1,
					width: '100%',
				}}
			>
				<Typography sx={{ mt: 1.5 }} variant="body2" color="text.secondary">
					Customs result:{' '}
				</Typography>
				{ordersData?.data[0]?.customsResult ? (
					<EasTableValue
						value={ordersData.data[0].customsResult}
						type={ETableStructureType.STATUS}
						color={ordersData?.data[0]?.customsResult ? 'success' : 'error'}
						tableStructureItem={tableStructure.find((item) => item.selector === 'customsResult')}
						size="l"
					/>
				) : (
					'—'
				)}
			</Box>
			<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
				<Typography sx={{ mt: 1.5 }} variant="body2" color="text.secondary">
					Customs passed at:
				</Typography>
				{renderValue(order.order.customs_passed_at, ETableStructureType.DATE_TIME)}
			</Box>
			<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
				<Typography sx={{ mt: 1.5 }} variant="body2" color="text.secondary">
					Customs sent at:
				</Typography>
				{renderValue(order.order.customs_sent_at, ETableStructureType.DATE_TIME)}
			</Box>
			<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
				<Typography sx={{ mt: 1.5 }} variant="body2" color="text.secondary">
					First mile tracking number:
				</Typography>
				{renderValue(order.order.tracking_number, ETableStructureType.COPYABLE_TEXT)}
			</Box>
			<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
				<Typography sx={{ mt: 1.5 }} variant="body2" color="text.secondary">
					Last mile tracking number:
				</Typography>
				{renderValue(order.order.last_mile_tracking_number, ETableStructureType.COPYABLE_TEXT)}
			</Box>

			<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
				<Typography sx={{ mt: 1.5 }} variant="body2" color="text.secondary">
					LRN:
				</Typography>
				{renderValue(order.order.customs_lrn_number, ETableStructureType.COPYABLE_TEXT)}
			</Box>
			<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
				<Typography sx={{ mt: 1.5 }} variant="body2" color="text.secondary">
					MRN Estonian Customs:
				</Typography>
				{renderValue(order.order.customs_mrn_number, ETableStructureType.COPYABLE_TEXT)}
			</Box>
		</EasCard>
	);
};
